<template>
  <div
    ref="rootRef"
    class="nav-item"
    :class="{
    'nav-item--active': isActive,
    'nav-item--focus-target': isFocusTarget,
    'nav-item--branch-active': isBranchActive,
    'nav-item--has-children': hasChildren,
    'nav-item--nested': level > 0
  }">
    <div
      class="nav-item-content"
      :class="{ 'nav-item-content--mini': isMini }"
      :style="indentStyle"
      :aria-label="displayName"
      :title="isMini ? displayName : undefined"
      @click="handleClick($event)"
    >
      <span class="nav-icon-plate">
        <i :class="getItemIcon(item)" class="nav-icon"></i>
      </span>
      <span class="nav-label" v-if="!isMini" :title="displayName">{{ displayName }}</span>

      <!-- Стрелка для элементов с детьми -->
      <i v-if="hasChildren && !isMini" class="pi pi-chevron-down nav-arrow"
        :class="{ 'nav-arrow--open': isOpen }"></i>
        <span v-if="!hasChildren && !isMini && counter !== undefined" class="nav-counter">
          {{ counter }}
        </span>
    </div>

    <!-- Рекурсивное отображение детей -->
    <div v-if="hasChildren && !isMini" v-show="isOpen" class="nav-children">
      <NavMenuItem v-for="child in item.children" :key="child.branch_id" :item="child" :level="level + 1"
        :is-mini="isMini" @item-click="$emit('item-click', $event)" />
    </div>
  </div>
</template>

<script setup lang="ts">
import { MenuTreeBulkKey, type MenuTreeBulkContext } from '@/components/app/menuTreeBulk'
import { registryFormIdFromSlotId } from '@/common/utils/registryFormIdFromSlotId'
import { useMenu } from '@/common/composables/useMenu'
import { useMenuFocusStore } from '@/stores/menuFocus'
import { storeToRefs } from 'pinia'
import { computed, inject, nextTick, ref, watch, type CSSProperties } from 'vue'
import { useRoute } from 'vue-router'

interface MenuItem {
  branch_id: number
  name: string
  /** false = collapsed until user/route opens (server `<open>0</open>`); omitted = expanded */
  open?: boolean
  children?: MenuItem[]
}

interface Props {
  item: MenuItem
  level?: number
  isMini?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  level: 0,
  isMini: false
})

const emit = defineEmits<{
  'item-click': [payload: { item: MenuItem; event: MouseEvent }]
}>()

const route = useRoute()
const bulk = inject(MenuTreeBulkKey, null) as MenuTreeBulkContext | null
const menuFocusStore = useMenuFocusStore()
const { focusedMenuId, focusEpoch } = storeToRefs(menuFocusStore)
const rootRef = ref<HTMLElement | null>(null)

function branchInitiallyExpanded(menuItem: MenuItem): boolean {
  if (!menuItem.children?.length) return false
  return menuItem.open !== false
}

const isOpen = ref(branchInitiallyExpanded(props.item))
const { getItemIcon, getItemCounter, formatItemName } = useMenu()

const hasChildren = computed(() => {
  return props.item.children && props.item.children.length > 0
})
const displayName = computed(() => formatItemName(props.item || ''))
const counter = computed(() => getItemCounter(props.item))

const hasActiveChildren = computed(() => {
  const activeMenuId = getActiveMenuRouteId()
  if (activeMenuId && hasActiveNode(props.item, activeMenuId)) return true
  if (focusedMenuId.value && hasActiveNode(props.item, focusedMenuId.value)) return true
  return false
})

watch(hasActiveChildren, (newVal) => {
  if (bulk?.suppressRouteExpansion.value) return
  if (newVal) {
    isOpen.value = newVal
  }
}, {immediate: true});

watch(
  () => bulk?.collapseSignal.value,
  () => {
    if (!bulk || !hasChildren.value) return
    isOpen.value = false
  },
)

watch(
  () => bulk?.expandSignal.value,
  () => {
    if (!bulk || !hasChildren.value) return
    isOpen.value = true
  },
)

watch(
  [focusedMenuId, focusEpoch],
  async ([id]) => {
    if (!id || String(props.item.branch_id) !== id) return
    if (hasChildren.value) isOpen.value = true
    await nextTick()
    rootRef.value?.scrollIntoView({ block: 'nearest', behavior: 'smooth' })
  },
  { immediate: true },
)


function hasActiveNode(node: MenuItem, currentId: string): boolean {
  let res = false;

  if (node.branch_id.toString() === currentId) {
    res = true;
  }

  if (node.children && node.children.length > 0) {
    node.children.forEach(node2 => {
      if (hasActiveNode(node2, currentId))
        res = true;
    });
  }
  return res;
}

const isFocusTarget = computed(() => {
  return Boolean(props.item.branch_id && focusedMenuId.value === props.item.branch_id.toString())
})

const isActive = computed(() => {
  const activeMenuId = getActiveMenuRouteId()
  if (props.item.branch_id && activeMenuId === props.item.branch_id.toString()) return true
  return isFocusTarget.value
})

const isBranchActive = computed(() => hasActiveChildren.value && !isActive.value)

const indentStyle = computed(() => {
  return {
    '--nav-indent': props.isMini ? '0rem' : `${props.level * 1.15}rem`,
  } as CSSProperties
})

function getActiveMenuRouteId(): string {
  if (route.name !== 'platform' && route.name !== 'document') return ''
  const routeId = route.params.id != null ? String(route.params.id) : ''
  if (!routeId) return ''
  return registryFormIdFromSlotId(routeId)
}

const handleClick = (event: MouseEvent) => {
  if (hasChildren.value) {
    if (bulk) {
      bulk.suppressRouteExpansion.value = false
    }
    isOpen.value = props.isMini ? true : !isOpen.value
  }
  emit('item-click', { item: props.item, event })
}

</script>

<style scoped lang="scss">
.nav-item {
  display: flex;
  flex-direction: column;
  cursor: pointer;

  &-content {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    min-height: 3rem;
    margin: 0 0.7rem;
    padding: 0.65rem 0.85rem 0.65rem calc(0.8rem + var(--nav-indent, 0rem));
    border-radius: 10px;
    color: inherit;
    transition: background-color 0.15s ease, color 0.15s ease;

    &:hover {
      background: rgba(255, 255, 255, 0.14);
    }
  }

  &-content--mini {
    align-self: center;
    width: 2.75rem;
    height: 2.75rem;
    min-height: 2.75rem;
    margin: 0;
    padding: 0;
    gap: 0;
    justify-content: center;
    background: transparent;
    border-radius: 14px;

    &:hover {
      background: transparent;
    }
  }

  &--active > .nav-item-content {
    background: #ffffff;
    color: var(--sidebar-accent, #1d4fd8);
    border-left: none;

    &:hover {
      background: #f4f7ff;
    }
  }

  &--active > .nav-item-content--mini,
  &--active > .nav-item-content--mini:hover {
    background: transparent;
  }

  &--branch-active > .nav-item-content .nav-icon-plate {
    background: rgba(255, 255, 255, 0.92);
    color: var(--sidebar-accent, #1d4fd8);
  }

  &--focus-target > .nav-item-content {
    outline: 2px solid rgba(255, 255, 255, 0.72);
    outline-offset: -2px;
  }

  &--nested {
    .nav-item-content {
      border-left: none;
    }
  }

  &--has-children > .nav-item-content {
    font-weight: 500;
  }
}

.nav-children {
  background: transparent;
  margin-left: 0;
  border-left: none;
}

.nav-icon {
  width: auto;
  font-size: 1.05rem;
  line-height: 1;
  text-align: center;
  color: inherit;
  flex-shrink: 0;
}

.nav-icon-plate {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 2.25rem;
  height: 2.25rem;
  border-radius: 10px;
  background: rgba(255, 255, 255, 0.16);
  color: inherit;
  flex-shrink: 0;
  transition: background-color 0.15s ease, color 0.15s ease;
}

.nav-item-content:hover .nav-icon-plate {
  background: rgba(255, 255, 255, 0.28);
}

.nav-item--active > .nav-item-content .nav-icon-plate,
.nav-item--active > .nav-item-content:hover .nav-icon-plate {
  background: color-mix(in srgb, var(--sidebar-accent, #1d4fd8) 10%, white);
  color: var(--sidebar-accent, #1d4fd8);
}

.nav-item-content--mini .nav-icon-plate {
  width: 2.5rem;
  height: 2.5rem;
  border-radius: 12px;
}

.nav-item--active > .nav-item-content--mini .nav-icon-plate,
.nav-item--active > .nav-item-content--mini:hover .nav-icon-plate {
  background: #ffffff;
  color: var(--sidebar-accent, #1d4fd8);
}

.nav-item--nested > .nav-item-content .nav-icon-plate {
  width: 1.85rem;
  height: 1.85rem;
  border-radius: 8px;
  background: rgba(255, 255, 255, 0.1);
}

.nav-label {
  font-weight: 500;
  font-size: 1.0625rem;
  line-height: 1.25;
  color: inherit;
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.nav-arrow {
  font-size: 0.75rem;
  opacity: 0.6;
  transition: transform 0.3s ease;
  margin-left: 0.15rem;
  flex-shrink: 0;

  &--open {
    transform: rotate(180deg);
  }
}

.nav-item--active > .nav-item-content .nav-arrow {
  opacity: 0.9;
}

.nav-counter {
  margin-left: auto;
  padding: 2px 8px;
  font-size: 0.75rem;
  font-weight: 600;
  background: rgba(255, 255, 255, 0.18);
  color: inherit;
  border-radius: 10px;
  min-width: 20px;
  text-align: center;
}

.nav-item--active > .nav-item-content .nav-counter {
  background: color-mix(in srgb, var(--sidebar-accent, #1d4fd8) 12%, white);
  color: var(--sidebar-accent, #1d4fd8);
}
</style>
